import { useCustomDashboard } from "$/lib/context/CustomDashboard.context";
import MaximizeIcon from "$/lib/icons/MaximizeIcon";
import MinimizeIcon from "$/lib/icons/MinimizeIcon";
import type { ThemeChoices } from "$/lib/types/General.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import React, { useState, type PropsWithChildren } from "react";
import { customColors } from "tailwind.config";
import { Loader } from "../../atoms/Loader/Loader";
import Flexbox from "../../common/Flexbox";
import WidgetButtons from "../../widgets/WidgetButtons";

interface Props {
  id?: string;
  title: string;
  Icon?: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  containerClassName?: string;
  className?: string;
  theme?: ThemeChoices;
  MiddleComponent?: React.ReactNode;
  isLoading?: boolean;
  isDraggable?: boolean;
  selectedDate?: Date;
  setSelectedDate?: (date: Date) => void;
}

const DisplayBox = ({
  id,
  children,
  title,
  Icon,
  className,
  containerClassName,
  theme = "blue",
  MiddleComponent,
  isLoading,
  isDraggable,
  selectedDate,
  setSelectedDate,
}: PropsWithChildren<Props>) => {
  const { handleDelete } = useCustomDashboard();

  const [expanded, setExpanded] = useState(true);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <Flexbox
      align="stretch"
      className={cn("rounded-2xl bg-white px-2 py-4", containerClassName)}
    >
      <Flexbox fullWidth fullHeight row align="center" className={cn("gap-2")}>
        <Flexbox row align="center" className={cn("gap-2 pl-2.5")}>
          {Icon && (
            <Flexbox
              row
              justify="center"
              align="center"
              className={cn("size-6 rounded-full", {
                "bg-secondary": theme === "blue",
                "bg-forest": theme === "green",
                "bg-warning": theme === "orange",
              })}
            >
              <Icon className="size-3 shrink [&_path]:!fill-white" />
            </Flexbox>
          )}
          <p
            className={cn("text-base font-bold", {
              "text-secondary": theme === "blue",
              "text-forest": theme === "green",
              "text-warning": theme === "orange",
            })}
          >
            {title}
          </p>
        </Flexbox>

        {expanded && !isLoading && MiddleComponent}

        <Flexbox row justify="end" className={cn("ml-auto mr-2 gap-2")}>
          {!isDraggable && (
            <button
              onClick={toggleExpanded}
              type="button"
              className="ml-auto cursor-pointer rounded-full bg-white p-2 shadow outline-none transition-shadow hover:shadow-lg"
            >
              {expanded ? (
                <MinimizeIcon
                  fill={
                    theme === "green"
                      ? customColors.forest.DEFAULT
                      : theme === "orange"
                        ? customColors.warning.DEFAULT
                        : customColors.secondary.DEFAULT
                  }
                />
              ) : (
                <MaximizeIcon
                  fill={
                    theme === "green"
                      ? customColors.forest.DEFAULT
                      : theme === "orange"
                        ? customColors.warning.DEFAULT
                        : customColors.secondary.DEFAULT
                  }
                />
              )}
            </button>
          )}

          {isDraggable && id && (
            <WidgetButtons
              id={id}
              theme={theme}
              expanded={expanded}
              handleToggle={toggleExpanded}
              handleDelete={() => handleDelete(id)}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          )}
        </Flexbox>
      </Flexbox>

      {isLoading ? (
        <Flexbox fullWidth align="center" justify="center">
          <Loader height={40} width={40} />
        </Flexbox>
      ) : (
        <div
          className={cn(
            "grid grid-rows-[0fr] transition-all",
            { "mt-4 grid-rows-[1fr]": expanded },
            expanded && className,
          )}
        >
          <div className={cn(!expanded && "overflow-hidden")}>{children}</div>
        </div>
      )}
    </Flexbox>
  );
};

export default DisplayBox;
